// ─── Impostazioni → 📄 Documenti del bot ─────────────────────────────────────
//
// Voce 37, dal mockup approvato il 27/09/2026 («37 AAA»). 🗣️ *«un indirizzo
// nascosto, tipo, dove possiamo pubblicare i documenti che la gente poi vede
// sul bot»*.
//
// Il regolamento dell'acconto e l'informativa privacy si scrivono qui; il
// gestionale li pubblica a un indirizzo che non si indovina, solo da leggere, e
// riempie da solo i campi che il bot legge. Qui ci entra solo chi può
// modificare le impostazioni; il socio legge dal link, senza entrare.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { TIPI_DOCUMENTO, testoInHtml, type Documento, type TipoDocumento } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'documenti-del-bot';

interface Pubblicato {
  documento: Documento;
  avviso: string | null;
}

/** «27/09/2026 alle 10:12» da `2026-09-27T10:12` (già all'ora di Roma). */
function quando(iso: string): string {
  const [g, o] = iso.split('T');
  const [a, m, d] = (g ?? '').split('-');
  return `${d}/${m}/${a}${o ? ` alle ${o}` : ''}`;
}

export function Documenti(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letti = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: Documento[] }>('GET', '/settings/documenti'),
  });
  const [tipo, setTipo] = useState<TipoDocumento>(() =>
    new URLSearchParams(window.location.search).get('tipo') === 'privacy' ? 'privacy' : 'regolamento',
  );
  if (letti.isLoading) return <Attesa cosa="i documenti del bot" />;
  if (letti.error || !letti.data) return <Guasto errore={letti.error} />;
  const doc = letti.data.items.find((d) => d.tipo === tipo)!;

  return (
    <section className="documenti-bot">
      <header className="testata">
        <h3>📄 Documenti del bot</h3>
        <p className="tenue">
          Qui si scrivono i documenti che il bot manda ai soci. Li scrive e li cambia solo chi può modificare
          le impostazioni; il socio li legge dal link che gli manda il bot, senza entrare nel gestionale.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      <div className="linguette-campi" role="tablist" aria-label="Quale documento">
        {TIPI_DOCUMENTO.map((t) => {
          const d = letti.data.items.find((x) => x.tipo === t)!;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tipo === t}
              className={tipo === t ? 'scelta' : undefined}
              onClick={() => setTipo(t)}
            >
              {d.titolo}
            </button>
          );
        })}
      </div>

      <IlDocumento key={doc.tipo} doc={doc} modificabile={modificabile} />
    </section>
  );
}

function IlDocumento({ doc, modificabile }: { doc: Documento; modificabile: boolean }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [testo, setTesto] = useState(doc.testo);
  const [nuova, setNuova] = useState(false);
  const [esito, setEsito] = useState<Pubblicato | null>(null);
  const [vedi, setVedi] = useState(false);
  useEffect(() => setTesto(doc.testo), [doc.testo]);

  const cambiato = testo.trim() !== doc.testo.trim();
  const corto = testo.trim().length < 20;
  // La prima volta non c'è niente da correggere: si pubblica la versione 1.
  const prima = doc.versione === null;

  const pubblica = useMutation({
    meta: { fatto: 'documento pubblicato' },
    mutationFn: () =>
      chiama<Pubblicato>('PUT', `/settings/documenti/${doc.tipo}`, { testo, nuovaVersione: prima || nuova }),
    onSuccess: (r) => {
      setEsito(r);
      setNuova(false);
      rileggi();
    },
  });

  return (
    <>
      <div className="riquadro">
        <h4>{doc.titolo}</h4>
        <dl className="coppie">
          <dt>Pubblicato</dt>
          <dd>
            {doc.versione === null || doc.pubblicatoIl === null ? (
              <span className="tenue">non ancora</span>
            ) : (
              <>
                versione <strong>{doc.versione}</strong> · {quando(doc.pubblicatoIl)}
              </>
            )}
          </dd>
          <dt>Indirizzo</dt>
          <dd>
            {doc.indirizzo ? (
              <>
                <a className="indirizzo" href={doc.indirizzo} target="_blank" rel="noreferrer">
                  {doc.indirizzo}
                </a>
                <br />
                <span className="tenue piccolo">
                  Nascosto: non si trova cercando, e i motori di ricerca non lo leggono. Lo conosce solo chi
                  riceve il link dal bot.
                </span>
              </>
            ) : (
              <span className="tenue">nasce alla prima pubblicazione</span>
            )}
          </dd>
        </dl>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (modificabile && cambiato && !corto) pubblica.mutate();
          }}
        >
          <fieldset disabled={!modificabile}>
            <label className="campo">
              <span>
                Il testo{' '}
                <span className="tenue">
                  (una riga che comincia con # diventa un titolo; una riga vuota va a capo)
                </span>
              </span>
              <textarea
                value={testo}
                rows={14}
                onChange={(e) => {
                  setTesto(e.target.value);
                  setEsito(null);
                }}
              />
            </label>
            {cambiato && corto && <p className="guasto">Il testo è troppo corto: almeno qualche riga.</p>}

            {!prima && (
              <div className="scelta-versione" role="radiogroup" aria-label="Che cosa pubblichi">
                <label>
                  <input type="radio" checked={!nuova} onChange={() => setNuova(false)} />{' '}
                  <span>
                    <strong>Correzione</strong>: un errore di battitura, niente che cambi le regole. Chi l’ha
                    già accettato non deve riaccettarlo.
                  </span>
                </label>
                <label>
                  <input type="radio" checked={nuova} onChange={() => setNuova(true)} />{' '}
                  <span>
                    <strong>Versione nuova</strong>: cambiano le regole.
                    {doc.tipo === 'regolamento'
                      ? ' Il bot lo fa riaccettare a tutti prima della loro prossima partita della sera.'
                      : ' Il numero sale.'}
                  </span>
                </label>
              </div>
            )}
          </fieldset>

          {modificabile && (
            <div className="riga-bottoni">
              <button type="submit" disabled={pubblica.isPending || !cambiato || corto}>
                {pubblica.isPending ? 'Un attimo…' : 'Pubblica'}
              </button>
              <button type="button" className="bordato" onClick={() => setVedi((v) => !v)}>
                {vedi ? 'Chiudi l’anteprima' : 'Come lo vede il socio'}
              </button>
            </div>
          )}
          <Guasto errore={pubblica.error} />
          {esito && !pubblica.isPending && (
            <p className={esito.avviso ? 'avvertenza' : 'tenue'}>
              {esito.avviso ??
                `Pubblicata la versione ${esito.documento.versione}. ${
                  doc.tipo === 'regolamento'
                    ? 'L’indirizzo e la versione sono già nell’Acconto.'
                    : 'L’indirizzo è già in «Cosa dice il bot ai soci».'
                }`}
            </p>
          )}
        </form>
      </div>

      {(vedi || !modificabile) && (
        <div className="riquadro come-lo-vede">
          <p className="tenue piccolo">Come lo vede il socio (con il testo scritto qui sopra)</p>
          <div className="pagina-socio">
            <div className="circolo">Padel Village</div>
            <h1>{doc.titolo}</h1>
            {/* 🔒 `testoInHtml` fa l'escape prima di tutto: è la stessa funzione della pagina vera. */}
            <div dangerouslySetInnerHTML={{ __html: testoInHtml(testo) }} />
          </div>
        </div>
      )}

      {doc.storia.length > 0 && (
        <div className="riquadro">
          <h4>Le versioni</h4>
          <ul className="storia">
            {doc.storia.map((v, i) => (
              <li key={i}>
                <span>
                  {v.correzione === 0 ? `versione ${v.versione}` : `correzione della ${v.versione}`}
                  {v.daChi ? ` · ${v.daChi}` : ''}
                </span>
                <span className="tenue">{quando(v.pubblicatoIl)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
